import React, { useState, useEffect, useRef } from "react";
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  ChevronDownIcon,
  InboxIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import type { Page, AuthenticatedUser, Notification } from "../types.ts";

import NotificationsPanel from "./NotificationsPanel.tsx";
import { supabase } from "../src/lib/supabaseClient";

interface HeaderProps {
  onMenuButtonClick: () => void;
  onNavigate: (page: Page, initialStateData?: any) => void;
  onLogout: () => void;
  onSearchOpen: () => void;
  currentUser: AuthenticatedUser | null;
}

const Header: React.FC<HeaderProps> = ({
  onMenuButtonClick,
  onNavigate,
  onLogout,
  onSearchOpen,
  currentUser,
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Memoized unread count to prevent redundant re-renders during boot
  const unreadCount = React.useMemo(
    () => notifications.filter((n: Notification) => !n.isRead).length,
    [notifications],
  );

  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState("English");

  const menuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  interface DbNotificationRow {
    id: string;
    user_id: string;
    title: string;
    category: Notification["category"];
    is_read?: boolean;
    isRead?: boolean;
    created_at: string;
    link?: { page: Page; initialState?: any };
  }

  // Phase 1: Immediate lightweight fetch (REST)
  useEffect(() => {
    const fetchNotifications = async () => {
      if (
        !currentUser?.id ||
        currentUser.id === "mock-client" ||
        currentUser.id === "mock-admin"
      )
        return;
      try {
        const { data, error } = await supabase
          .from("notifications")
          .select("*")
          .eq("user_id", currentUser.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        const normalized: Notification[] = (
          (data as DbNotificationRow[]) || []
        ).map((row) => ({
          id: row.id,
          title: row.title,
          category: row.category,
          isRead: Boolean(row.is_read ?? row.isRead ?? false),
          timestamp: row.created_at,
          link: row.link,
        }));
        setNotifications(normalized);
      } catch (error) {
        // Silent error in production to maintain premium experience
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to fetch notifications:", error);
        }
      }
    };
    fetchNotifications();
  }, [currentUser?.id]);

  // Phase 2: Adaptive Realtime Initialization
  useEffect(() => {
    let subscription: any = null;
    let isMounted = true;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let adaptiveTimer: any = null;

    const setupSubscription = () => {
      if (!currentUser?.id || !isMounted) return;

      // Cleanup previous if exists
      if (subscription) supabase.removeChannel(subscription);

      subscription = supabase
        .channel("notifications_channel")
        .on(
          // @ts-ignore
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${currentUser.id}`,
          },
          (payload) => {
            const row = payload.new as DbNotificationRow;
            const normalized: Notification = {
              id: row.id,
              title: row.title,
              category: row.category,
              isRead: Boolean(row.is_read ?? row.isRead ?? false),
              timestamp: row.created_at,
              link: row.link,
            };
            setNotifications((prev) => [normalized, ...prev]);
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            if (process.env.NODE_ENV === "development") {
              console.log("[Header] Realtime notifications initialized");
            }
          }
          if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            // Downgrade to debug/silent warning for cleaner production console
            if (process.env.NODE_ENV === "development") {
              console.debug(
                "[Header] Realtime connectivity issue, retrying...",
              );
            }
            if (isMounted) {
              supabase.removeChannel(subscription);
              retryTimeout = setTimeout(setupSubscription, 10000);
            }
          }
        });
    };

    // ADAPTIVE TRIGGER: Fast devices use Idle time, Slow devices use 800ms fallback
    // @ts-ignore
    if (window.requestIdleCallback) {
      // @ts-ignore
      adaptiveTimer = window.requestIdleCallback(() => setupSubscription(), {
        timeout: 800,
      });
    } else {
      adaptiveTimer = setTimeout(setupSubscription, 800);
    }

    return () => {
      isMounted = false;
      // @ts-ignore
      if (window.cancelIdleCallback) window.cancelIdleCallback(adaptiveTimer);
      else clearTimeout(adaptiveTimer);

      if (retryTimeout) clearTimeout(retryTimeout);
      if (subscription) supabase.removeChannel(subscription);
    };
  }, [currentUser?.id]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setIsNotificationsOpen(false);
      }
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setIsLanguageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, notificationsRef, languageRef]);

  const handleMenuNavigation = (page: Page, initialStateData?: any) => {
    onNavigate(page, initialStateData);
    setIsUserMenuOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser?.id) return;
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", currentUser.id);

      if (error) throw error;

      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Failed to mark notifications as read:", error);
      }
    }
  };

  return (
    <header className="sticky top-0 z-40 flex-shrink-0 bg-white/80 backdrop-blur-md border-b border-border/30 shadow-sm transition-all duration-200">
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-16 h-16 max-w-[1400px] mx-auto w-full">
        {/* Mobile Menu Button & Search */}
        <div className="flex items-center flex-1">
          <button
            onClick={onMenuButtonClick}
            className="lg:hidden text-text p-2 -ml-2 mr-3 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Open sidebar"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="relative hidden md:block w-full max-w-lg">
            <button
              onClick={onSearchOpen}
              className="group flex items-center w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm text-text-subtle text-left hover:bg-white hover:border-primary/30 hover:shadow-md hover:ring-2 hover:ring-primary/10 transition-all duration-200 ease-in-out"
            >
              <MagnifyingGlassIcon className="h-5 w-5 mr-3 text-gray-400 group-hover:text-primary transition-colors" />
              <span className="flex-1">
                Search orders, products, customers...
              </span>
              <kbd className="hidden lg:inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-medium text-gray-500 font-sans shadow-sm">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
          </div>
        </div>

        {/* Right side icons and user menu */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Language Selector */}
          <div className="relative hidden md:block" ref={languageRef}>
            <button
              onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              className={`flex items-center gap-2 text-text-subtle hover:text-text p-2 rounded-lg hover:bg-gray-100 transition-colors relative ${isLanguageMenuOpen ? "bg-gray-100 text-text" : ""}`}
              aria-label="Select Language"
            >
              <GlobeAltIcon className="h-5 w-5" />
              <span className="text-sm font-medium hidden xl:block">
                {currentLanguage}
              </span>
              <ChevronDownIcon
                className={`h-3 w-3 transition-transform duration-200 ${isLanguageMenuOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isLanguageMenuOpen && (
              <div className="absolute right-0 mt-2 w-40 origin-top-right rounded-xl bg-white shadow-xl ring-1 ring-gray-200/50 focus:outline-none z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="py-1">
                  {["English", "Spanish", "French", "German"].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setCurrentLanguage(lang);
                        setIsLanguageMenuOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${currentLanguage === lang ? "text-primary font-medium bg-primary/5" : "text-text"}`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Inbox Button */}
          <button
            onClick={() => onNavigate("Unified Inbox")}
            className="text-text-subtle hover:text-text hover:text-primary p-2.5 rounded-full hover:bg-primary/5 transition-all duration-200 relative group"
            aria-label="Unified Inbox"
          >
            <InboxIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>

          {/* Notification Button & Panel */}
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className={`text-text-subtle hover:text-text hover:text-primary p-2.5 rounded-full hover:bg-primary/5 transition-all duration-200 relative group ${isNotificationsOpen ? "text-primary bg-primary/5" : ""}`}
              aria-label={`You have ${unreadCount} unread notifications`}
            >
              <BellIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-status-error opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-status-error"></span>
                </span>
              )}
            </button>
            {isNotificationsOpen && (
              <NotificationsPanel
                notifications={notifications}
                onClose={() => setIsNotificationsOpen(false)}
                onNavigate={onNavigate}
                onMarkAllAsRead={handleMarkAllAsRead}
              />
            )}
          </div>

          <div className="h-8 w-px bg-gray-200 mx-1 hidden sm:block"></div>

          {/* User Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 pl-1 pr-2 py-1 rounded-full hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/20"
            >
              <img
                className="h-9 w-9 rounded-full ring-2 ring-white shadow-sm"
                src={
                  currentUser?.avatarUrl ||
                  "https://picsum.photos/seed/default/100/100"
                }
                alt="User avatar"
              />
              <div className="hidden lg:block text-left">
                <p className="text-sm font-semibold text-text leading-tight">
                  {currentUser
                    ? `${currentUser.firstName} ${currentUser.lastName}`
                    : "User"}
                </p>
                <p className="text-[11px] font-medium text-text-subtle uppercase tracking-wide">
                  {currentUser?.title || "Role"}
                </p>
              </div>
              <ChevronDownIcon
                className={`h-4 w-4 text-text-subtle hidden lg:block transition-transform duration-200 ${isUserMenuOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-3 w-72 origin-top-right rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200/50 focus:outline-none z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-4 bg-gray-50/50 rounded-t-2xl border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <img
                      className="h-12 w-12 rounded-full ring-4 ring-white shadow-sm"
                      src={
                        currentUser?.avatarUrl ||
                        "https://picsum.photos/seed/default/100/100"
                      }
                      alt="User avatar"
                    />
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {currentUser
                          ? `${currentUser.firstName} ${currentUser.lastName}`
                          : "User"}
                      </p>
                      <p className="text-xs text-gray-500 font-medium">
                        {currentUser?.email || ""}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded textxs font-medium bg-green-100 text-green-800 mt-1">
                        Active
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-2 space-y-1">
                  <p className="px-3 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Account
                  </p>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuNavigation("Settings", {
                        initialTab: "profile",
                      });
                    }}
                    className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary rounded-lg transition-colors"
                  >
                    My Profile
                  </a>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuNavigation("Settings", {
                        initialTab: "notifications",
                      });
                    }}
                    className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary rounded-lg transition-colors"
                  >
                    Notification Preferences
                  </a>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuNavigation("Settings", {
                        initialTab: "security",
                      });
                    }}
                    className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary rounded-lg transition-colors"
                  >
                    Security Settings
                  </a>
                </div>
                <div className="h-px bg-gray-100 my-1 mx-2" />
                <div className="p-2 space-y-1">
                  <p className="px-3 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Billing
                  </p>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuNavigation("Billing");
                    }}
                    className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary rounded-lg transition-colors"
                  >
                    Subscription & Billing
                  </a>
                </div>
                <div className="h-px bg-gray-100 my-1 mx-2" />
                <div className="p-2">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onLogout();
                      setIsUserMenuOpen(false);
                    }}
                    className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-status-error hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Sign out
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
